import { cloneDeep } from 'lodash';
import { ITestSetupContentComponent, IValueChangedEvent } from './test-setup.content';
import { REMOVED } from './base.setup.tab';
import { BaseSetupTab } from './base.setup.tab';

export abstract class TestSetupComponent {
  createContentComponent(index: number): void {
    const id = this.getSetupService().currentTab.config.def.template;
    const cType = this.resolveComponentType(id);
    this.getContentViewRef(index).clear();
    const component = this.getContentViewRef(index).createComponent(cType);
    const casted = component.instance as ITestSetupContentComponent;

    casted.tab = this.getSetupService().currentTab;
    casted.setupCtrl = this;
    casted.componentId = id;
    // notice I'm cloning value to give access to full object/but without ability to mess with the content of original stuff.
    casted.previousOutput = cloneDeep(casted.tab.parent.output);
    casted.stateHistory = (componentId, keyId) => {
      const stateId = this.buildComponentIdentifier(componentId, keyId);
      return this.getSetupService().currentTab.stateOfExtendedControls[stateId];
    };

    casted.otherTabStateHistory = (componentId, keyId) => {
      const stateId = this.buildComponentIdentifier(componentId, keyId);
      const index = this.getSetupService().tabs.findIndex(
        item => item.config.def.template === componentId
      );
      return this.getSetupService().tabs[index].stateOfExtendedControls[stateId];
    };

    if (casted.onDataValueChange) {
      casted.onDataValueChange.subscribe(event =>
        this.onDataValueChangeListener(this.getSetupService().currentTab, event)
      );
    }
    if (casted.onOtherTabDataValueChange) {
      casted.onOtherTabDataValueChange.subscribe(event => {
        const index = this.getSetupService().tabs.findIndex(
          item => item.config.def.template === event.componentId
        );
        this.onDataValueChangeListener(this.getSetupService().tabs[index], event);
      });
    }
    if (casted.onMetaDataValueChange) {
      casted.onMetaDataValueChange.subscribe(event =>
        this.onMetaDataValueChangeListener(this.getSetupService().currentTab, event)
      );
    }
    casted.restoreValues = () => {
      for (const key of Object.keys(this.getSetupTab().output)) {
        const nameValue = casted.stateHistory(casted.componentId, key);
        if (nameValue && nameValue.value && nameValue.value !== REMOVED) {
          casted[key] = nameValue.value;
        }
      }
    };

    casted.restoreAgentBasedConfig = (agentType: string, testMode: string, version: string) => {
      const currenTabIndex = this.getSetupService().currentTab.config.index;
      this.getSetupService().restoreAgentBasedConfig(agentType, testMode, version);
      this.toggleTabChange(currenTabIndex);
      casted.tab = this.getSetupService().currentTab;
    };
  }

  onDataValueChangeListener(currentTab: BaseSetupTab, event: IValueChangedEvent): void {
    currentTab.dataValueChange(
      this.buildComponentIdentifier(event.componentId, event.key),
      event.key,
      event.newValue,
      event.isValid
    );
  }

  onMetaDataValueChangeListener(currentTab: BaseSetupTab, event: IValueChangedEvent): void {
    currentTab.metaDataValueChange(
      this.buildComponentIdentifier(event.componentId, event.key),
      event.key,
      event.newValue,
      event.isValid
    );
  }

  buildComponentIdentifier(componentId: string, keyId?: string): string {
    // create inputId based on keyId, to get the values in edit mode using stateHistory
    const inputId = this.buildInputIdentifier(keyId);
    return `${componentId}/${inputId}`;
  }

  buildInputIdentifier(inputId?: string): string {
    return `${inputId}Input`;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  abstract getSetupService();
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  abstract getSetupTab();
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  abstract resolveComponentType(templateId: any);
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  abstract getContentViewRef(index: number);
  abstract toggleTabChange(index: number);
}
